import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { buildMonthDays } from "../lib/monthCalendar";
import { compactRecordValue, localDateKey } from "../lib/records";
import { recordVisuals } from "../lib/recordVisuals";
import type { HealthRecord } from "../types";
import { usePreferences } from "../preferences/usePreferences";
import { ChevronLeftIcon, ChevronRightIcon } from "./Icons";
import { RecordKindIcon } from "./RecordKindIcon";

interface MonthCalendarProps {
  month: Date;
  now: Date;
  records: HealthRecord[];
  selectedDate?: string;
  onMonthChange: (month: Date) => void;
  onSelectDate: (date: string) => void;
}

export function MonthCalendar({ month, now, records, selectedDate, onMonthChange, onSelectDate }: MonthCalendarProps) {
  const { t, i18n } = useTranslation();
  const { weightUnit } = usePreferences();
  const locale = i18n.resolvedLanguage ?? "en";
  const days = useMemo(() => buildMonthDays(month, now, records), [month, now, records]);
  const monthLabel = capitalizeFirst(new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(month), locale);
  const currentMonth = month.getFullYear() === now.getFullYear() && month.getMonth() === now.getMonth();
  const [keyboardDate, setKeyboardDate] = useState<string>();
  const focusRequestRef = useRef<{ date: string; view: "mobile" | "desktop" } | undefined>(undefined);
  const activeDate = days.some((day) => day.inMonth && day.key === keyboardDate)
    ? keyboardDate
    : days.find((day) => day.inMonth && day.key === selectedDate)?.key
      ?? days.find((day) => day.inMonth && day.today)?.key
      ?? days.find((day) => day.inMonth)?.key;
  const weekdayLabels = useMemo(() => {
    const monday = new Date(2026, 0, 5);
    return Array.from({ length: 7 }, (_, index) => {
      const date = new Date(monday);
      date.setDate(monday.getDate() + index);
      return new Intl.DateTimeFormat(locale, { weekday: "short" }).format(date);
    });
  }, [locale]);
  const weeks = useMemo(() => Array.from({ length: 6 }, (_, index) => days.slice(index * 7, index * 7 + 7)), [days]);

  useEffect(() => {
    const focusRequest = focusRequestRef.current;
    if (!focusRequest) return;
    const target = document.querySelector<HTMLButtonElement>(`[data-calendar-view="${focusRequest.view}"] [data-date-key="${focusRequest.date}"]`);
    if (!target) return;
    target.focus();
    focusRequestRef.current = undefined;
  }, [days]);

  function moveMonth(offset: number) {
    onMonthChange(new Date(month.getFullYear(), month.getMonth() + offset, 1));
  }

  function selectDay(dayKey: string) {
    setKeyboardDate(dayKey);
    onSelectDate(dayKey);
  }

  function handleDayKeyDown(event: KeyboardEvent<HTMLButtonElement>, dayKey: string) {
    const current = parseDateKey(dayKey);
    if (!current) return;

    let target: Date;
    switch (event.key) {
      case "ArrowLeft":
        target = addDays(current, -1);
        break;
      case "ArrowRight":
        target = addDays(current, 1);
        break;
      case "ArrowUp":
        target = addDays(current, -7);
        break;
      case "ArrowDown":
        target = addDays(current, 7);
        break;
      case "Home":
        target = addDays(current, -mondayIndex(current));
        break;
      case "End":
        target = addDays(current, 6 - mondayIndex(current));
        break;
      case "PageUp":
        target = addMonths(current, event.shiftKey ? -12 : -1);
        break;
      case "PageDown":
        target = addMonths(current, event.shiftKey ? 12 : 1);
        break;
      default:
        return;
    }

    event.preventDefault();
    const targetKey = localDateKey(target);
    const view = event.currentTarget.closest<HTMLElement>("[data-calendar-view]")?.dataset.calendarView;
    const resolvedView = view === "mobile" ? "mobile" : "desktop";
    setKeyboardDate(targetKey);
    if (target.getFullYear() !== month.getFullYear() || target.getMonth() !== month.getMonth()) {
      focusRequestRef.current = { date: targetKey, view: resolvedView };
      onMonthChange(new Date(target.getFullYear(), target.getMonth(), 1));
    } else {
      window.requestAnimationFrame(() => document.querySelector<HTMLButtonElement>(`[data-calendar-view="${resolvedView}"] [data-date-key="${targetKey}"]`)?.focus());
    }
  }

  return (
    <section aria-labelledby="calendar-heading" className="surface-elevated overflow-hidden rounded-[1.4rem]">
      <div className="flex flex-col gap-4 px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <div>
          <h2 id="calendar-heading" className="text-2xl font-semibold tracking-[-0.035em] sm:text-3xl">{monthLabel}</h2>
          <p className="mt-1 text-sm text-muted">{t("calendar.monthDescription")}</p>
        </div>
        <div className="flex items-center gap-2" aria-label={t("calendar.navigation")}>
          <button type="button" onClick={() => moveMonth(-1)} aria-label={t("calendar.previousMonth")} className="pressable grid size-11 place-items-center rounded-xl border border-line bg-surface text-muted hover:border-line-strong hover:bg-surface-raised hover:text-ink focus-ring">
            <ChevronLeftIcon className="size-4" />
          </button>
          <button type="button" onClick={() => onMonthChange(new Date(now.getFullYear(), now.getMonth(), 1))} disabled={currentMonth} className="pressable min-h-11 rounded-xl border border-teal-700 bg-teal-700 px-4 text-sm font-bold text-canvas hover:bg-teal-800 focus-ring disabled:cursor-default disabled:border-line disabled:bg-surface disabled:text-muted disabled:opacity-60">
            {t("calendar.today")}
          </button>
          <button type="button" onClick={() => moveMonth(1)} aria-label={t("calendar.nextMonth")} className="pressable grid size-11 place-items-center rounded-xl border border-line bg-surface text-muted hover:border-line-strong hover:bg-surface-raised hover:text-ink focus-ring">
            <ChevronRightIcon className="size-4" />
          </button>
        </div>
      </div>

      <div data-calendar-view="mobile" role="grid" aria-labelledby="calendar-heading" className="border-t border-line sm:hidden">
        <div role="row" className="grid grid-cols-7 border-b border-line bg-surface-raised/60 text-center text-xs font-black uppercase tracking-[0.08em] text-muted">
          {weekdayLabels.map((weekday, index) => <span role="columnheader" key={`${weekday}-${index}`} className="px-1 py-2.5">{weekday.slice(0, 2)}</span>)}
        </div>
        {weeks.map((week) => (
            <div role="row" className="grid grid-cols-7 gap-px bg-line" key={`mobile-week-${week[0]?.key}`}>
              {week.map((day) => {
                const dateLabel = new Intl.DateTimeFormat(locale, { dateStyle: "full" }).format(day.date);
                return (
                  <div role="gridcell" className="min-w-0 bg-surface" key={day.key}>
                    <button
                      type="button"
                      data-date-key={day.key}
                      disabled={!day.inMonth}
                      tabIndex={day.inMonth && day.key === activeDate ? 0 : -1}
                      onClick={() => selectDay(day.key)}
                      onKeyDown={(event) => handleDayKeyDown(event, day.key)}
                      aria-haspopup={day.inMonth ? "dialog" : undefined}
                      aria-expanded={day.inMonth ? day.key === selectedDate : undefined}
                      aria-current={day.today ? "date" : undefined}
                      aria-label={t("calendar.selectDate", { date: dateLabel, records: t("calendar.records", { count: day.records.length }) })}
                      className={`pressable relative grid min-h-12 w-full place-items-center bg-surface px-1 py-1.5 focus-ring ${day.inMonth ? "hover:bg-surface-raised" : "cursor-default bg-canvas text-muted/55"} ${day.key === selectedDate ? "z-[1] bg-teal-50 ring-2 ring-inset ring-teal-600" : ""}`}
                    >
                      <span className={`grid size-7 place-items-center rounded-lg text-xs font-bold tabular-nums ${day.today ? "bg-ink text-canvas" : day.inMonth ? "text-ink" : "text-muted/55"}`}>{day.day}</span>
                      {day.inMonth && day.records.length > 0 ? (
                        <span aria-hidden="true" className="absolute bottom-1 flex max-w-[2.25rem] gap-0.5">
                          {day.records.slice(0, 3).map((record) => <span key={record.id} className={`size-1 rounded-full ${recordVisuals[record.kind].dotClass}`} />)}
                        </span>
                      ) : null}
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
      </div>

      <div className="calendar-scroll-region hidden overflow-x-auto border-t border-line sm:block">
        <div data-calendar-view="desktop" role="grid" aria-labelledby="calendar-heading" className="min-w-[52rem]">
          <div role="row" className="grid grid-cols-7 border-b border-line bg-surface-raised/60 text-center text-xs font-black uppercase tracking-[0.1em] text-muted">
            {weekdayLabels.map((weekday, index) => <span role="columnheader" key={`${weekday}-${index}`} className="px-2 py-3">{weekday}</span>)}
          </div>
          {weeks.map((week) => (
              <div role="row" className="grid grid-cols-7 gap-px bg-line" key={`desktop-week-${week[0]?.key}`}>
                {week.map((day) => {
                  const visibleRecords = day.records.slice(0, 2);
                  const remaining = day.records.length - visibleRecords.length;
                  const dateLabel = new Intl.DateTimeFormat(locale, { dateStyle: "full" }).format(day.date);
                  return (
                    <div role="gridcell" className="min-w-0 bg-surface" key={day.key}>
                    <button
                      type="button"
                      data-date-key={day.key}
                      disabled={!day.inMonth}
                      tabIndex={day.inMonth && day.key === activeDate ? 0 : -1}
                      onClick={() => selectDay(day.key)}
                      onKeyDown={(event) => handleDayKeyDown(event, day.key)}
                      aria-haspopup={day.inMonth ? "dialog" : undefined}
                      aria-expanded={day.inMonth ? day.key === selectedDate : undefined}
                      aria-current={day.today ? "date" : undefined}
                      aria-label={t("calendar.selectDate", { date: dateLabel, records: t("calendar.records", { count: day.records.length }) })}
                      className={`group relative flex min-h-20 w-full flex-col bg-surface p-3 text-left transition-[background-color,box-shadow] duration-200 focus-ring ${day.inMonth ? "hover:z-10 hover:bg-surface-raised hover:shadow-[inset_0_0_0_1px_var(--color-line-strong)]" : "cursor-default bg-canvas text-muted/55"} ${day.today ? "z-[1] ring-2 ring-inset ring-teal-600" : ""} ${day.key === selectedDate ? "bg-teal-50" : ""}`}
                    >
                      <span className={`ml-auto grid min-w-7 place-items-center rounded-lg px-1.5 py-1 text-xs font-bold tabular-nums ${day.today ? "bg-ink text-canvas" : day.inMonth ? "text-ink" : "text-muted/55"}`}>{day.day}</span>
                      {day.inMonth ? (
                        <span className="mt-auto w-full space-y-1.5">
                          {visibleRecords.map((record) => (
                            <span key={record.id} className={`flex min-w-0 items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-bold ${recordVisuals[record.kind].chipClass}`}>
                              <RecordKindIcon kind={record.kind} className="size-4 shrink-0" />
                              <span className="truncate">{compactRecordValue(record, locale, weightUnit)}</span>
                            </span>
                          ))}
                          {remaining > 0 ? <span className="block px-2 text-xs font-bold text-muted">{t("calendar.moreRecords", { count: remaining })}</span> : null}
                        </span>
                      ) : null}
                    </button>
                    </div>
                  );
                })}
              </div>
            ))}
        </div>
      </div>
    </section>
  );
}

function capitalizeFirst(value: string, locale: string): string {
  return value.charAt(0).toLocaleUpperCase(locale) + value.slice(1);
}

function parseDateKey(value: string): Date | undefined {
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day, 12);
}

function addDays(date: Date, amount: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + amount);
  return result;
}

function addMonths(date: Date, amount: number): Date {
  const targetMonth = new Date(date.getFullYear(), date.getMonth() + amount, 1, 12);
  const lastDay = new Date(targetMonth.getFullYear(), targetMonth.getMonth() + 1, 0).getDate();
  targetMonth.setDate(Math.min(date.getDate(), lastDay));
  return targetMonth;
}

function mondayIndex(date: Date): number {
  return (date.getDay() + 6) % 7;
}
